import * as React from "react";
import { ISessionState } from "source/models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import { HorizontalBars } from "ui/Scripts/source/components/framework/HorizontalBars";
import { AppUtils } from "source/framework/appUtils";
import * as riskService from "source/models/services/riskService";
import { List } from "source/components/framework/List/List";
import { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { IRiskListItem, RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { RiskListLevelChip } from "../RiskList/RiskListLevelChip";
import { getReportFieldValueNbr, getRiskPageGeneralFilterFromFilterState, getShowFilterCondition, IRiskPageFilterLists, IRiskPageFilterState } from "./RiskPageUtils";
import { RiskListTraceabilityChip } from "../RiskList/RiskListTraceabilityChip";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { IRiskAssessmentEditItem } from "ui/Scripts/source/models/riskAssessment/riskAssessmentEditItem";
import { Base } from "ui/Scripts/source/framework/base";
import { ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IReportItem } from "source/models/report/reportItem";
import { TBarSerie } from "ui/Scripts/source/models/common/barSerie";
import { EnumHelper, RiskType } from "ui/Scripts/source/models/enums";
import { TRiskIdIdCode } from "ui/Scripts/source/models/risk/riskIdIdCode";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { HtmlReportPanel } from "./HtmlReportPanel";
import { RiskPageReportHtmlFilterCondition } from "./RiskPageReportHtmlFilterCondition";
import { RiskPageReportHtmlContainer } from "./RiskPageReportHtmlContainer";

interface IHtmlReportTextFieldProp {
    title: string;
    text: React.ReactNode;
    marginBottom?: string;
}

const HtmlReportTextField = (props: IHtmlReportTextFieldProp) => {
    return (
        <Box sx={{ marginBottom: props.marginBottom || "1rem" }}>
            <Typography variant="overline" color="text.secondary">{props.title}</Typography>
            <Typography component="div" variant="body2" color="text.secondary">
                {props.text}
            </Typography>
        </Box>
    );
};

interface IRiskPageReportHtmlExtensiveRiskReportProp extends IRiskPageFilterLists, IRiskPageFilterState {
    sessionState: ISessionState;
    companyId: string;
    settings: IRiskSettingEdit;
    report: IReportItem;
    dataId?: string;
    isPrinting: boolean;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    onSetInitialized: (initialized: boolean) => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

interface IRiskPageReportHtmlExtensiveRiskReportState {
    intialized: boolean;
    pageSize: number;
    page: number;
    items: IRiskListItem[];
    riskCategoryCounts: TBarSerie[];
    riskLevelCounts: TBarSerie[];
    assessments: Record<string, IRiskAssessmentEditItem>;
    riskProcesses: Record<string, string[]>;
    riskStandards: Record<string, string[]>;
    riskStakeholders: Record<string, string[]>;
    //Visibilty options
    showFilterCondition: boolean;
    showRiskRegisterSummary: boolean;
    showRiskRegisterDetails: boolean;
    showRiskTypings: boolean;
    showDescription: boolean;
    showRiskThreat: boolean;
    showAssessment: boolean;
    showRiskControls: boolean;
    showRiskTreatments: boolean;
    showApproval: boolean;
}

const riskLevels = [
    { text: Translations.RiskLevelCritical, color: RiskListItem.getRiskLevelColor(15) },
    { text: Translations.RiskLevelHigh, color: RiskListItem.getRiskLevelColor(14) },
    { text: Translations.RiskLevelMedium, color: RiskListItem.getRiskLevelColor(9) },
    { text: Translations.RiskLevelLow, color: RiskListItem.getRiskLevelColor(6) },
    { text: Translations.RiskLevelNegligible, color: RiskListItem.getRiskLevelColor(3) },
];

export const RiskPageReportHtmlExtensiveRiskReport = (props: IRiskPageReportHtmlExtensiveRiskReportProp) => {
    const _isMounted = useRef(false);
    const loadAssessmentDataId = useRef(0);
    const loadRiskTypingsDataId = useRef(0);

    const [state, setState] = useState<IRiskPageReportHtmlExtensiveRiskReportState>({
        intialized: false,
        pageSize: 10000,
        page: 1,
        items: [],
        riskCategoryCounts: [],
        riskLevelCounts: [],
        assessments: {},
        riskProcesses: {},
        riskStandards: {},
        riskStakeholders: {},
        showFilterCondition: false,
        showRiskRegisterSummary: false,
        showRiskRegisterDetails: false,
        showRiskTypings: false,
        showDescription: false,
        showRiskThreat: false,
        showAssessment: false,
        showRiskControls: false,
        showRiskTreatments: false,
        showApproval: false,
    });

    useEffect(() => {
        _isMounted.current = true;

        return () => {
            _isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        props.onSetInitialized(false);
    }, [props.companyId]);

    useEffect(() => {
        if (!props.report?.taskDataRow) return;
        setState(old => ({
            ...old,
            showFilterCondition: getShowFilterCondition(props.report),
            showRiskRegisterSummary: getReportFieldValueNbr(props.report, "RiskRegisterSummary") > 0,
            showRiskRegisterDetails: getReportFieldValueNbr(props.report, "RiskRegisterDetails") > 0,
            showRiskTypings: getReportFieldValueNbr(props.report, "RiskTypings") > 0,
            showDescription: getReportFieldValueNbr(props.report, "Description") > 0,
            showRiskThreat: getReportFieldValueNbr(props.report, "RiskThreat") > 0,
            showAssessment: getReportFieldValueNbr(props.report, "Assessment") > 0,
            showRiskControls: getReportFieldValueNbr(props.report, "RiskControls") > 0,
            showRiskTreatments: getReportFieldValueNbr(props.report, "RiskTreatments") > 0,
            showApproval: getReportFieldValueNbr(props.report, "Approval") > 0,
        }));
    }, [props.report?.taskDataRow]);

    const getRiskCounts = (items: IRiskListItem[]): {
        riskCategoryCounts: TBarSerie[],
        riskLevelCounts: TBarSerie[],
    } => {
        const riskCategoryCounts: Record<string, number> = {};
        const riskLevelCounts: Record<string, number> = {};
        items.forEach(item => {
            if (item.riskCategoryCode) {
                riskCategoryCounts[item.riskCategoryCode] = (riskCategoryCounts[item.riskCategoryCode] || 0) + 1;
            }
            if (item.hasAssesment) {
                const riskLevelTitle = RiskListItem.getRiskLevelTextBasic(item.residualRisk);
                if (riskLevelTitle) {
                    riskLevelCounts[riskLevelTitle] = (riskLevelCounts[riskLevelTitle] || 0) + 1;
                }
            }
        });
        return {
            riskCategoryCounts: Object.entries(riskCategoryCounts)
                .map(i => ({
                    title: i[0],
                    amount: i[1],
                    color: "#8fa8df",
                    backgroundColor: "#edf0f4"
                }))
                .sort((a, b) => {
                    const result = -1 * Base.numCompare(a.amount, b.amount);
                    if (result !== 0) return result;
                    return Base.strCompare(a.title, b.title);
                }),
            riskLevelCounts: riskLevels.map((level) => {
                const count = riskLevelCounts[level.text] ?? 0;
                return {
                    title: level.text,
                    amount: count,
                    color: level.color,
                    backgroundColor: "#edf0f4"
                };
            }),
        };
    };

    useEffect(() => {
        const loadAssessmentData = async (assessmentIds: string[]) => {
            const batchSize = 100;
            const batches = [];
            const loadAssessmentDataIdCurrent = loadAssessmentDataId.current;
            for (let i = 0; i < assessmentIds.length; i += batchSize) {
                batches.push(assessmentIds.slice(i, i + batchSize));
            }
            for (const batch of batches) {
                if (!_isMounted.current || loadAssessmentDataId.current !== loadAssessmentDataIdCurrent) break;
                const assessmentsResult = await AppUtils.callService(() => riskService.getRiskAssessmentEditItems(props.companyId, batch), false);
                if (!assessmentsResult) continue;
                if (!_isMounted.current || loadAssessmentDataId.current !== loadAssessmentDataIdCurrent) break;
                for (const item of assessmentsResult.items) {
                    item.personsInCharge = ModifyUser.sortModifyUsers(item.personsInCharge, { currentUserCode: props.sessionState.code });
                }
                setState((old) => ({
                    ...old,
                    assessments: {
                        ...old.assessments,
                        ...Object.fromEntries(assessmentsResult.items.map(item => [item.id, item])),
                    },
                }));
            }
        };

        const getGroupedRiskTypings = (riskTypingsResult: TRiskIdIdCode[]): Record<string, string[]> => {
            const result: Record<string, string[]> = {};
            if (riskTypingsResult) {
                for (const item of riskTypingsResult) {
                    if (!item.riskId || !item.code) continue;
                    if (!result[item.riskId]) {
                        result[item.riskId] = [];
                    }
                    result[item.riskId].push(item.code);
                }
            }
            return result;
        };

        const loadRiskTypingsData = async (riskIds: string[]) => {
            const batchSize = 100;
            const batches = [];
            const loadRiskTypingsDataIdCurrent = loadRiskTypingsDataId.current;
            for (let i = 0; i < riskIds.length; i += batchSize) {
                batches.push(riskIds.slice(i, i + batchSize));
            }
            for (const batch of batches) {
                if (!_isMounted.current || loadRiskTypingsDataId.current !== loadRiskTypingsDataIdCurrent) break;
                const riskTypingsResult = await AppUtils.callService(() => riskService.getRiskTypings(props.companyId, batch), false);
                if (!riskTypingsResult) continue;
                if (!_isMounted.current || loadRiskTypingsDataId.current !== loadRiskTypingsDataIdCurrent) break;
                setState((old) => ({
                    ...old,
                    riskProcesses: {
                        ...old.riskProcesses,
                        ...getGroupedRiskTypings(riskTypingsResult.riskProcesses),
                    },
                    riskStandards: {
                        ...old.riskStandards,
                        ...getGroupedRiskTypings(riskTypingsResult.riskStandards),
                    },
                    riskStakeholders: {
                        ...old.riskStakeholders,
                        ...getGroupedRiskTypings(riskTypingsResult.riskStakeholders),
                    },
                }));
            }
        };

        const loadData = async () => {
            const generalFilter = getRiskPageGeneralFilterFromFilterState(props);
            const result = await AppUtils.callService(() => riskService.getRiskListItems(props.companyId, state.pageSize, 1,
                false, null, generalFilter.typeIds, generalFilter.unitIds, generalFilter.stateIds, generalFilter.riskCategoryIds, generalFilter.riskProcessIds,
                generalFilter.riskStandardIds, generalFilter.riskStakeholderIds, generalFilter.residualRiskIds, generalFilter.riskTreatmentStateIds,
                generalFilter.handlerUserCodes, generalFilter.owners, props.sortColumn, props.sortOrderIsAsc), false);
            if (!result) return;
            setState((old) => ({
                ...old,
                intialized: true,
                items: result.items,
                ...getRiskCounts(result.items),
            }));
            props.onSetInitialized(true);
            loadAssessmentDataId.current++;
            loadAssessmentData(result.items.map(i => i.riskAssessmentId).filter(id => id));
            loadRiskTypingsDataId.current++;
            loadRiskTypingsData(result.items.map(i => i.id));
        };

        if (!props.companyId) return;
        loadData();
    }, [props.companyId,
        props.dataId,
        props.selectedRiskTypeIds.join(","),
        props.selectedUnitIds.join(","),
        props.selectedStateIds.join(","),
        props.selectedRiskCategoryIds.join(","),
        props.selectedRiskProcessIds.join(","),
        props.selectedRiskStandardIds.join(","),
        props.selectedRiskStakeholderIds.join(","),
        props.selectedResidualRiskIds.join(","),
        props.selectedRiskTreatmentStateIds.join(","),
        props.selectedHandlerUserCodes.join(","),
        props.selectedOwners.join(",")]);

    const sortList = (column: string, asc: boolean) => {
        const items = state.items.slice(0);
        RiskListItem.sortRiskListItemsByColumn(items, column, asc);
        props.onSetSortData(column, asc);
        setState(old => ({
            ...old,
            items
        }));
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, _nextPage: boolean) => {
        sortList(sortColumn, sortOrderIsAsc);
    };

    const risks = state.items.filter(item => item.riskType === RiskType.Risk);
    const opportunities = state.items.filter(item => item.riskType === RiskType.Opportunity);

    const getRiskRegisterDetails = (isPrinting: boolean, isRisks: boolean) => {
        const items = isRisks ? risks : opportunities;
        if (!items.length) return null;
        
        return (
            <Box sx={{ marginTop: 2, breakInside: "avoid" }}>
                <HtmlReportPanel
                    title={isRisks ? Translations.RiskRegisterDetails : Translations.OpportunityRegisterDetails}
                    height={isPrinting ? "auto" : undefined}
                    isPrinting={isPrinting}
                >
                    {items.map((item, index) => {
                        const isRisk = item.riskType === RiskType.Risk;
                        const assessment = state.assessments[item.riskAssessmentId];
                        const riskProcesses = state.riskProcesses[item.id] || [];
                        const riskStandards = state.riskStandards[item.id] || [];
                        const riskStakeholders = state.riskStakeholders[item.id] || [];
                        const hasAssessment = !!assessment;
                        return (
                            <Card key={item.id} variant="outlined" sx={{ height: isPrinting ? "auto" : "100%", marginTop: index > 0 ? 2 : undefined, breakInside: "avoid" }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "action.hover", padding: ".5rem 1rem" }}>
                                    <Box>
                                        <Typography variant="h6" component="div">
                                            {item.title}
                                        </Typography>
                                        {state.showRiskTypings &&
                                            <Box>
                                                <Typography component="div" variant="caption" color="text.secondary">
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        <Box>{`${Translations.RiskCategory}: ${item.riskCategoryCode}`}</Box>
                                                        <Box>{`${Translations.RiskOwner}: ${item.owner}`}</Box>
                                                        {!!riskStandards.length &&
                                                            <Box>{`${Translations.RiskStandards}: ${riskStandards.join(", ")}`}</Box>
                                                        }
                                                        {!!riskStakeholders.length &&
                                                            <Box>{`${Translations.RiskStakeholders}: ${riskStakeholders.join(", ")}`}</Box>
                                                        }
                                                        {!!riskProcesses.length &&
                                                            <Box>{`${Translations.RiskProcesses}: ${riskProcesses.join(", ")}`}</Box>
                                                        }
                                                    </Box>
                                                </Typography>
                                            </Box>
                                        }
                                    </Box>
                                    {item.hasAssesment &&
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <RiskListLevelChip riskLevel={item.riskLevel} />
                                            <Box><TrendingFlatIcon /></Box>
                                            <RiskListLevelChip riskLevel={item.residualRisk} />
                                            <Box><TrendingFlatIcon /></Box>
                                            <RiskListTraceabilityChip riskLevel={item.residualRisk} />
                                        </Box>
                                    }
                                </Box>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        {state.showDescription &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField title={isRisk ? Translations.Description : Translations.OpportunityDescription} text={isRisk ? item.description : item.opportunityDescription}/>
                                            </Grid>
                                        }
                                        {state.showRiskThreat &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField title={isRisk ? Translations.RiskThreat : Translations.OpportunityBenefit} text={isRisk ? item.threat : item.opportunityBenefit} />
                                            </Grid>
                                        }
                                        {isRisk && state.showRiskControls &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField title={Translations.RiskControls} text={item.controls} />
                                            </Grid>
                                        }
                                        {state.showAssessment && hasAssessment &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField
                                                    title={Translations.Assessment}
                                                    text={
                                                        <Box>
                                                            <Box>{assessment.comment}</Box>
                                                            <Box sx={{ marginTop: ".5rem" }}>
                                                                <Typography component="div" variant="caption" color="text.secondary">
                                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 8px" }}>
                                                                        <Box>{`${Translations.Likelihood}: ${RiskListItem.getRiskLikelihoodText(assessment.likelihood)}`}</Box>
                                                                        <Box>{`${Translations.Impact}: ${RiskListItem.getRiskImpactText(assessment.impact)}`}</Box>
                                                                        <Box>{`${Translations.ControlEffiency}: ${RiskListItem.getRiskControlEfficiencyText(assessment.controlEffiency)}`}</Box>
                                                                    </Box>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    marginBottom="0"
                                                />
                                            </Grid>
                                        }
                                        {state.showRiskTreatments && hasAssessment &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField
                                                    title={Translations.RiskTreatments}
                                                    text={
                                                        <Box>
                                                            <Box>{assessment.treatments}</Box>
                                                            <Box sx={{ marginTop: ".5rem" }}>
                                                                <Typography component="div" variant="caption" color="text.secondary">
                                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 8px" }}>
                                                                        <Box>{`${Translations.TreatmentTargetDate}: ${Base.utcTimeToDateStr(assessment.treatmentTargetDate)}`}</Box>
                                                                        <Box>{`${Translations.TreatmentEfficiency}: ${assessment.treatmentEfficiency.toString()} %`}</Box>
                                                                        <Box>{`${Translations.PersonInCharge}: ${assessment.personsInCharge.map(i => i.name).join(", ")}`}</Box>
                                                                    </Box>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    marginBottom="0"
                                                />
                                            </Grid>
                                        }
                                        {state.showApproval && hasAssessment &&
                                            <Grid item sm={6}>
                                                <HtmlReportTextField
                                                    title={Translations.Approval}
                                                    text={
                                                        <Box>
                                                            <Box>{assessment.approvalJustification}</Box>
                                                            <Box sx={{ marginTop: ".5rem" }}>
                                                                <Typography component="div" variant="caption" color="text.secondary">
                                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 8px" }}>
                                                                        {!Base.isNullOrUndefined(assessment.approval) &&
                                                                            <Box>{`${Translations.ApprovalDate}: ${Base.utcTimeToDateStr(assessment.approvalDate)}`}</Box>
                                                                        }
                                                                        <Box>{`${Translations.RiskApproval}: ${EnumHelper.getApprovalTypeListText(assessment.approval)}`}</Box>
                                                                        {!Base.isNullOrUndefined(assessment.approval) &&
                                                                            <Box>{`${Translations.ApprovalAuthority}: ${assessment.approvalAuthority}`}</Box>
                                                                        }
                                                                    </Box>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    marginBottom="0"
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </CardContent>
                            </Card>
                        );
                    })}
                </HtmlReportPanel>
            </Box>
        );
    };

    const getContent = (isPrinting: boolean) => {
        return (<>
            {state.showFilterCondition &&
                <RiskPageReportHtmlFilterCondition
                    isPrinting={isPrinting}
                    settings={props.settings}
                    {...props}
                />
            }
            <Grid container spacing={2} sx={{ display: "flex", alignItems: "stretch" }}>
                <Grid item sm={6}>
                    <HtmlReportPanel
                        title={Translations.ByRiskCategory}
                        isPrinting={isPrinting}
                    >
                        <HorizontalBars
                            series={state.riskCategoryCounts}
                        />
                    </HtmlReportPanel>
                </Grid>
                <Grid item sm={6}>
                    <HtmlReportPanel
                        title={Translations.Distribution}
                        isPrinting={isPrinting}
                    >
                        <HorizontalBars
                            series={state.riskLevelCounts}
                        />
                    </HtmlReportPanel>
                </Grid>
            </Grid>
            {state.showRiskRegisterSummary &&
                <>
                    {risks.length > 0 &&
                        <Box sx={{ marginTop: 2 }}>
                            <HtmlReportPanel
                                title={`${Translations.RiskRegisterSummary} / ${Translations.Risks}`}
                                height={isPrinting ? "auto" : undefined}
                                isPrinting={isPrinting}
                            >
                                <List
                                    intialized={true}
                                    isLoading={false}
                                    hasMore={false}
                                    height="auto"
                                    header={{
                                        columns: [
                                            { size: 4, column: "title", content: Translations.Title },
                                            { size: 2, column: "riskCategoryCode", content: Translations.RiskCategory },
                                            { size: 2, column: "owner", content: Translations.RiskOwner },
                                            { size: 2, column: "residualRisk", content: Translations.ResidualRisk },
                                            { size: 2, column: "riskTreatmentState", content: Translations.RiskTreatments }
                                        ]
                                    }}
                                    lines={
                                        risks.map((item) => {
                                            return {
                                                columns: [
                                                    { size: 4, content: <Box>{item.title}</Box> },
                                                    { size: 2, content: <Box>{item.riskCategoryCode}</Box> },
                                                    { size: 2, content: <Box>{item.owner}</Box> },
                                                    { size: 2, content: item.hasAssesment ? <RiskListLevelChip riskLevel={item.residualRisk} /> : <Box>{Translations.Missing}</Box> },
                                                    { size: 2, content: item.hasAssesment ? <Box>{RiskListItem.getRiskTreatmentStateText(item.riskTreatmentState)}</Box> : null },
                                                ],
                                                id: item.id
                                            };
                                        })
                                    }
                                    sortColumn={props.sortColumn}
                                    sortOrderIsAsc={props.sortOrderIsAsc}
                                    selectedId={""}
                                    checkedIds={[]}
                                    onSetSelectedIds={() => {}}
                                    onSearchLines={handleSearchLines}
                                />
                            </HtmlReportPanel>
                        </Box>
                    }
                    {opportunities.length > 0 &&
                        <Box sx={{ marginTop: 2, breakInside: "avoid" }}>
                            <HtmlReportPanel
                                title={`${Translations.RiskRegisterSummary} / ${Translations.Opportunities}`}
                                height={isPrinting ? "auto" : undefined}
                                isPrinting={isPrinting}
                            >
                                <List
                                    intialized={true}
                                    isLoading={false}
                                    hasMore={false}
                                    height="auto"
                                    header={{
                                        columns: [
                                            { size: 4, column: "title", content: Translations.Title },
                                            { size: 4, column: "riskCategoryCode", content: Translations.RiskCategory },
                                            { size: 4, column: "owner", content: Translations.RiskOwner },
                                        ]
                                    }}
                                    lines={
                                        opportunities.map((item) => {
                                            return {
                                                columns: [
                                                    { size: 4, content: <Box>{item.title}</Box> },
                                                    { size: 4, content: <Box>{item.riskCategoryCode}</Box> },
                                                    { size: 4, content: <Box>{item.owner}</Box> },
                                                ],
                                                id: item.id
                                            };
                                        })
                                    }
                                    sortColumn={props.sortColumn}
                                    sortOrderIsAsc={props.sortOrderIsAsc}
                                    selectedId={""}
                                    checkedIds={[]}
                                    onSetSelectedIds={() => {}}
                                    onSearchLines={handleSearchLines}
                                />
                            </HtmlReportPanel>
                        </Box>
                    }
                </>
            }
            {state.showRiskRegisterDetails &&
                <>
                    {getRiskRegisterDetails(isPrinting, true)}
                    {getRiskRegisterDetails(isPrinting, false)}
                </>
            }
        </>);
    };

    if (!state.intialized) return null;

    return (
        <RiskPageReportHtmlContainer
            isPrinting={props.isPrinting}
            getContent={getContent}
        />
    );
};